import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Zap, ArrowRight, Shield, Lock, Eye, Activity, Layers, Terminal,
  ChevronRight, TrendingUp, TrendingDown, AlertTriangle, Globe,
  Cpu, BarChart3, Radio, MonitorSmartphone, Server, FileText,
  ShieldCheck, KeyRound, ScrollText, Plug, BrainCircuit, Workflow,
} from "lucide-react";

// ─── Helpers ───────────────────────────────────────────────────────
const CLIP_16 = "polygon(16px 0%, calc(100% - 16px) 0%, 100% 16px, 100% calc(100% - 16px), calc(100% - 16px) 100%, 16px 100%, 0% calc(100% - 16px), 0% 16px)";
const CLIP_8 = "polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0% calc(100% - 8px), 0% 8px)";
const CLIP_6 = "polygon(6px 0%, calc(100% - 6px) 0%, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0% calc(100% - 6px), 0% 6px)";

function FadeIn({ children, className = "", delay = 0, y = 30 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function SectionLabel({ text }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-1.5 h-1.5 bg-[#2563EB] rounded-full" />
      <span style={{
        fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", fontWeight: "600",
        letterSpacing: "0.2em", color: "#475569", textTransform: "uppercase",
      }}>{text}</span>
    </div>
  );
}

function OctaCard({ children, className = "", hover = false }) {
  return (
    <div style={{ clipPath: CLIP_16, background: "rgba(255,255,255,0.045)", padding: "1px" }}>
      <div
        style={{ clipPath: CLIP_16, background: "linear-gradient(180deg, rgba(22,25,32,1) 0%, rgba(17,19,24,1) 100%)" }}
        className={`relative overflow-hidden ${hover ? "transition-all duration-300 hover:brightness-[1.06]" : ""} ${className}`}
      >
        <div className="absolute top-0 left-0 right-0 pointer-events-none" style={{
          height: "1px", background: "linear-gradient(90deg, transparent 10%, rgba(255,255,255,0.07) 50%, transparent 90%)",
        }} />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(180deg, transparent 65%, rgba(0,0,0,0.15) 100%)",
        }} />
        <div className="relative">{children}</div>
      </div>
    </div>
  );
}

// ─── 1. HEADER ─────────────────────────────────────────────────────
function Header() {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-[100] px-4 sm:px-8 py-3"
      style={{ background: "rgba(11,13,16,0.85)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
      data-testid="landing-header"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-[#2563EB]" style={{ clipPath: CLIP_6 }}>
            <Zap size={14} className="text-white" strokeWidth={2.5} />
          </div>
          <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", fontSize: "14px", letterSpacing: "0.15em", color: "#fff" }}>ACE OS</span>
        </div>
        <nav className="hidden md:flex items-center gap-8">
          {[
            { label: "Product", id: "solution" },
            { label: "How It Works", id: "how-it-works" },
            { label: "Security", id: "trust" },
            { label: "Roadmap", id: "roadmap" },
          ].map(item => (
            <button key={item.id} onClick={() => scrollToSection(item.id)}
              className="transition-colors duration-200 hover:text-[#E2E8F0]"
              style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "12px", fontWeight: "500", color: "#94A3B8", letterSpacing: "0.04em" }}>
              {item.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="landing-signin-btn"
            className="hidden sm:block transition-colors duration-200 hover:text-[#E2E8F0]"
            style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "12px", fontWeight: "500", color: "#94A3B8" }}>
            Sign In
          </Link>
          <Link to="/signup" data-testid="landing-cta-btn"
            className="transition-all duration-200 hover:brightness-110"
            style={{ clipPath: CLIP_6, background: "#2563EB", padding: "8px 20px", fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", letterSpacing: "0.1em", color: "#fff" }}>
            GET EARLY ACCESS
          </Link>
        </div>
      </div>
    </motion.header>
  );
}

// ─── Terminal Typing Effect ────────────────────────────────────────
const TERMINAL_MESSAGES = [
  "Monitoring Binance Futures...",
  "Syncing MT5 Alpha...",
  "Portfolio healthy — 3 venues online",
  "BTCUSDT: +$619.53 unrealized",
  "Margin utilization: 17.5%",
  "All systems operational",
];

function TerminalTyper() {
  const [msgIdx, setMsgIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const msg = TERMINAL_MESSAGES[msgIdx];
    if (!isDeleting && charIdx < msg.length) {
      const t = setTimeout(() => setCharIdx(c => c + 1), 35 + Math.random() * 25);
      return () => clearTimeout(t);
    }
    if (!isDeleting && charIdx === msg.length) {
      const t = setTimeout(() => setIsDeleting(true), 2200);
      return () => clearTimeout(t);
    }
    if (isDeleting && charIdx > 0) {
      const t = setTimeout(() => setCharIdx(c => c - 1), 15);
      return () => clearTimeout(t);
    }
    if (isDeleting && charIdx === 0) {
      setIsDeleting(false);
      setMsgIdx(i => (i + 1) % TERMINAL_MESSAGES.length);
    }
  }, [charIdx, isDeleting, msgIdx]);

  const text = TERMINAL_MESSAGES[msgIdx].slice(0, charIdx);

  return (
    <div className="flex items-center gap-2 mt-5 px-3 py-2" data-testid="terminal-typer"
      style={{ background: "rgba(11,13,16,0.7)", border: "1px solid rgba(255,255,255,0.04)", display: "inline-flex" }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#2563EB", fontWeight: "600" }}>$</span>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#94A3B8", fontVariantNumeric: "tabular-nums", minWidth: "200px" }}>
        {text}<span className="animate-pulse" style={{ color: "#2563EB" }}>|</span>
      </span>
    </div>
  );
}

// ─── LIVE Product Preview (feels alive) ────────────────────────────
function LiveProductMockup() {
  // Subconscious motion — numbers drift very slowly
  const [tick, setTick] = useState(0);
  const [microFlicker, setMicroFlicker] = useState(false);

  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 3500);
    return () => clearInterval(iv);
  }, []);

  // Rare micro-flicker: ~every 8-12 seconds, a brief opacity dip
  useEffect(() => {
    const flickerInterval = setInterval(() => {
      if (Math.random() > 0.6) {
        setMicroFlicker(true);
        setTimeout(() => setMicroFlicker(false), 150);
      }
    }, 8000);
    return () => clearInterval(flickerInterval);
  }, []);

  // Subtle number drift
  const drift = (base, range) => (base + Math.sin(tick * 0.7 + base) * range).toFixed(2);
  const totalEquity = drift(102362, 80);
  const totalPnl = drift(1456, 30);
  const syncSec = (tick * 3) % 30 + 2;

  const platforms = [
    { name: "Binance", status: "LIVE", equity: drift(52407, 40), pnl: drift(566, 15), pnlPos: true, positions: 4, margin: "17.5%", risk: "LOW", color: "#F0B90B" },
    { name: "Bybit", status: "LIVE", equity: drift(31204, 25), pnl: drift(1203, 20), pnlPos: true, positions: 3, margin: "24.1%", risk: "LOW", color: "#F7A600" },
    { name: "MT5 Alpha", status: "SYNCING", equity: drift(18750, 15), pnl: drift(-312, 8), pnlPos: false, positions: 2, margin: "41.2%", risk: "MODERATE", color: "#2563EB" },
  ];

  // Animated sparkline — continuous slow crawl
  const sparkPoints = Array.from({ length: 12 }, (_, i) => {
    const y = 6 + Math.sin((tick * 0.15) + i * 0.6) * 3 - (i * 0.25);
    return `${i * 3.6},${Math.max(1, Math.min(11, y))}`;
  }).join(" ");

  // Per-platform mini sparklines
  const platSpark = (seed) => Array.from({ length: 8 }, (_, i) => {
    const y = 5 + Math.sin((tick * 0.2) + i * 0.9 + seed) * 2.5;
    return `${i * 4},${Math.max(1, Math.min(9, y))}`;
  }).join(" ");

  return (
    <div className="relative" style={{ opacity: microFlicker ? 0.92 : 1, transition: "opacity 0.15s ease" }}>
      {/* Fake top bar */}
      <div className="mb-3 flex items-center gap-2 px-3 py-2" style={{ background: "rgba(11,13,16,0.95)", border: "1px solid rgba(255,255,255,0.03)" }}>
        <div className="flex items-center gap-1.5">
          <div className="live-dot" style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#22C55E", boxShadow: "0 0 6px rgba(34,197,94,0.3)" }} />
          <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "7px", color: "#22C55E", letterSpacing: "0.12em", fontWeight: "600" }}>LIVE</span>
        </div>
        <div style={{ width: "1px", height: "12px", background: "rgba(255,255,255,0.04)" }} />
        <AnimatePresence mode="wait">
          <motion.span key={totalEquity} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }}
            style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: "#E2E8F0", fontVariantNumeric: "tabular-nums" }}>
            ${totalEquity}
          </motion.span>
        </AnimatePresence>
        <AnimatePresence mode="wait">
          <motion.span key={totalPnl} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }}
            style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: "#22C55E", fontVariantNumeric: "tabular-nums" }}>
            +${totalPnl}
          </motion.span>
        </AnimatePresence>
        <div className="ml-auto flex items-center gap-2">
          <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "7px", color: "#475569", letterSpacing: "0.1em" }}>3 PLATFORMS</span>
          <svg width="44" height="12" viewBox="0 0 44 12">
            <polyline points={sparkPoints} fill="none" stroke="#22C55E" strokeWidth="0.8" strokeLinecap="round" style={{ transition: "all 2.5s ease" }} />
          </svg>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "6px", color: "#334155", fontVariantNumeric: "tabular-nums" }}>{syncSec}s</span>
        </div>
      </div>
      {/* Platform panels */}
      <div className="grid grid-cols-3 gap-2">
        {platforms.map((p, i) => (
          <div key={i} style={{ clipPath: CLIP_8, background: "rgba(255,255,255,0.025)", padding: "1px" }}>
            <div style={{ clipPath: CLIP_8, background: "linear-gradient(180deg, rgba(22,25,32,1), rgba(15,17,21,1))" }} className="p-3 relative overflow-hidden">
              {/* Subtle platform tint */}
              <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at top left, ${p.color}05, transparent 70%)` }} />
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "9px", color: "#E2E8F0" }}>{p.name}</span>
                  <div className="flex items-center gap-1">
                    <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: p.status === "LIVE" ? "#22C55E" : "#38BDF8", boxShadow: `0 0 4px ${p.status === "LIVE" ? "rgba(34,197,94,0.3)" : "rgba(56,189,248,0.3)"}` }}
                      className={p.status === "LIVE" ? "live-dot" : ""} />
                    <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "6px", color: p.status === "LIVE" ? "#22C55E" : "#38BDF8", letterSpacing: "0.1em", fontWeight: "600" }}>{p.status}</span>
                  </div>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div key={p.equity} initial={{ opacity: 0.5 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", fontWeight: "700", color: "#E2E8F0", fontVariantNumeric: "tabular-nums" }}>
                    ${p.equity}
                  </motion.div>
                </AnimatePresence>
                <AnimatePresence mode="wait">
                  <motion.div key={p.pnl} initial={{ opacity: 0.5 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: p.pnlPos ? "#22C55E" : "#EF4444", fontVariantNumeric: "tabular-nums", marginTop: "2px" }}>
                    {p.pnlPos ? "+" : ""}{p.pnl} USDT
                  </motion.div>
                </AnimatePresence>
                <div style={{ height: "1px", background: "rgba(255,255,255,0.03)", margin: "8px 0 6px" }} />
                <div className="flex items-center justify-between">
                  <div className="flex gap-3">
                    <div><div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "6px", color: "#334155", letterSpacing: "0.1em" }}>POS</div><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#94A3B8", fontVariantNumeric: "tabular-nums" }}>{p.positions}</div></div>
                    <div><div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "6px", color: "#334155", letterSpacing: "0.1em" }}>MARGIN</div><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#38BDF8", fontVariantNumeric: "tabular-nums" }}>{p.margin}</div></div>
                    <div><div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "6px", color: "#334155", letterSpacing: "0.1em" }}>RISK</div><div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", fontWeight: "600", color: p.risk === "LOW" ? "#22C55E" : "#F59E0B" }}>{p.risk}</div></div>
                  </div>
                  {/* Per-platform micro sparkline */}
                  <svg width="28" height="10" viewBox="0 0 28 10" style={{ opacity: 0.4 }}>
                    <polyline points={platSpark(i * 2.3)} fill="none" stroke={p.pnlPos ? "#22C55E" : "#EF4444"} strokeWidth="0.6" strokeLinecap="round" style={{ transition: "all 2.5s ease" }} />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── 2. HERO ───────────────────────────────────────────────────────
function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-16 overflow-hidden" data-testid="hero-section">
      {/* Background — FIX 2: Depth composition */}
      <motion.div className="absolute inset-0 pointer-events-none" style={{ y: bgY }}>
        {/* Deep radial glow behind UI area */}
        <div style={{ position: "absolute", top: "5%", right: "5%", width: "800px", height: "700px", background: "radial-gradient(ellipse, rgba(37,99,235,0.07) 0%, rgba(37,99,235,0.02) 40%, transparent 70%)", filter: "blur(60px)" }} />
        <div style={{ position: "absolute", top: "-10%", left: "30%", width: "500px", height: "500px", background: "radial-gradient(circle, rgba(37,99,235,0.05) 0%, transparent 70%)", filter: "blur(80px)" }} />
        <div style={{ position: "absolute", bottom: "10%", left: "5%", width: "300px", height: "300px", background: "radial-gradient(circle, rgba(56,189,248,0.03) 0%, transparent 70%)", filter: "blur(60px)" }} />
      </motion.div>
      {/* Vignette — FIX 2 */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 80% 70% at 50% 50%, transparent 50%, rgba(11,13,16,0.4) 100%)" }} />

      {/* FIX 4: Unified environment — content overlaps with UI */}
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-0 items-center">

          {/* Left: Copy — 5 cols */}
          <div className="lg:col-span-5 relative z-20">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <SectionLabel text="Unified Trading Command System" />
            </motion.div>

            {/* FIX 3: Tighter headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
              style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "800", lineHeight: 1.04, marginBottom: "20px", letterSpacing: "-0.01em" }}
              className="text-4xl sm:text-5xl lg:text-[3.2rem]"
            >
              <span style={{ color: "#fff" }}>All Your Trading Platforms.</span>
              <br />
              <span style={{ color: "#2563EB" }}>One Command Layer.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
              style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "15px", lineHeight: 1.7, marginBottom: "28px", maxWidth: "420px" }}
            >
              Connect your crypto exchanges, forex brokers, and trading accounts into one unified command system. Monitor, manage, and act — from a single interface.
            </motion.p>

            {/* FIX 5: Premium CTA — tactile, not clickable */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="flex flex-wrap gap-3 mb-5">
              <Link to="/signup" data-testid="hero-cta-btn"
                className="flex items-center gap-2 group hero-cta-primary"
                style={{
                  clipPath: CLIP_8, background: "rgba(37,99,235,0.85)", padding: "13px 26px",
                  fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "12px", letterSpacing: "0.1em", color: "#fff",
                  position: "relative", overflow: "hidden",
                }}>
                <span className="relative z-10 flex items-center gap-2 transition-transform duration-500 group-hover:translate-x-[1px]">
                  GET EARLY ACCESS <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-0.5" />
                </span>
              </Link>
              <button onClick={() => document.getElementById("product-preview")?.scrollIntoView({ behavior: "smooth" })}
                className="flex items-center gap-2 group"
                style={{
                  clipPath: CLIP_8, border: "1px solid rgba(255,255,255,0.06)", padding: "13px 22px",
                  fontFamily: "'Inter Tight', sans-serif", fontWeight: "500", fontSize: "12px", color: "#64748B",
                  transition: "all 0.5s cubic-bezier(0.22,1,0.36,1)",
                }}>
                <Eye size={14} /> PRODUCT PREVIEW
              </button>
            </motion.div>

            {/* FIX 6: Trust micro-strip */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.7 }}
              className="flex items-center gap-4 flex-wrap"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#475569" }}
            >
              {["Secure API connection", "No custody", "No withdrawal access"].map((item, i) => (
                <span key={item} className="flex items-center gap-1.5">
                  {i === 0 && <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: "#22C55E", boxShadow: "0 0 4px rgba(34,197,94,0.3)" }} />}
                  {i > 0 && <span style={{ color: "#1E293B" }}>·</span>}
                  {item}
                </span>
              ))}
            </motion.div>

            {/* Terminal typing effect */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.9 }}>
              <TerminalTyper />
            </motion.div>

            {/* FIX 3: System signal — "this is not a website, this is a system" */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.2 }}
              className="mt-4" data-testid="system-signal"
            >
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", letterSpacing: "0.08em",
                color: "#334155", lineHeight: 1.6,
              }}>
                <span style={{ color: "#475569", fontWeight: "600", letterSpacing: "0.16em" }}>SYSTEM STATUS</span>
                <br />
                <span>All Platforms Synced</span>
                <span style={{ color: "#1E293B" }}> · </span>
                <span>Risk Stable</span>
                <span style={{ color: "#1E293B" }}> · </span>
                <span style={{ color: "#22C55E40" }}>No Active Alerts</span>
              </div>
            </motion.div>
          </div>

          {/* Right: LIVE Product mockup — 7 cols, bleeds into darkness — FIX 1,2,4 */}
          <motion.div
            initial={{ opacity: 0, x: 30, scale: 0.98 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block lg:col-span-7 relative"
            style={{ marginLeft: "-40px", marginRight: "-80px" }}
          >
            {/* Deep glow behind — FIX 2 */}
            <div style={{
              position: "absolute", inset: "-60px",
              background: "radial-gradient(ellipse 70% 60% at 50% 40%, rgba(37,99,235,0.06), transparent 70%)",
              filter: "blur(30px)", pointerEvents: "none",
            }} />

            {/* 3D perspective container — no border box */}
            <div className="relative" style={{ perspective: "1200px" }}>
              <div style={{
                transform: "rotateY(-2deg) rotateX(1deg)",
                transformOrigin: "center center",
                background: "transparent",
                padding: "16px",
                transition: "transform 0.6s ease",
              }}>
                <LiveProductMockup />
              </div>
            </div>

            {/* Fade edges into darkness — break the rectangle */}
            <div style={{
              position: "absolute", top: 0, right: 0, bottom: 0, width: "120px",
              background: "linear-gradient(90deg, transparent, rgba(11,13,16,0.7) 40%, rgba(11,13,16,1))",
              pointerEvents: "none", zIndex: 10,
            }} />
            <div style={{
              position: "absolute", left: 0, right: 0, bottom: 0, height: "80px",
              background: "linear-gradient(0deg, rgba(11,13,16,1), rgba(11,13,16,0.5) 40%, transparent)",
              pointerEvents: "none", zIndex: 10,
            }} />
            <div style={{
              position: "absolute", left: 0, right: 0, top: 0, height: "40px",
              background: "linear-gradient(180deg, rgba(11,13,16,0.6), transparent)",
              pointerEvents: "none", zIndex: 10,
            }} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── 3. PROBLEM ────────────────────────────────────────────────────
function ProblemSection() {
  const pains = [
    { icon: MonitorSmartphone, title: "Too Many Tabs", desc: "Juggling 5 browser windows, 3 mobile apps, and 2 desktop terminals just to see your full portfolio." },
    { icon: AlertTriangle, title: "Scattered Risk", desc: "Your exposure is fragmented. A margin call on one platform while you are looking at another." },
    { icon: Eye, title: "No Unified View", desc: "No single screen shows your total equity, combined PnL, and cross-platform margin usage." },
    { icon: Activity, title: "Slow Reactions", desc: "Switching between platforms costs seconds. In volatile markets, seconds cost money." },
  ];

  return (
    <section className="py-24 sm:py-32 relative" id="problem" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <FadeIn>
          <SectionLabel text="The Problem" />
          <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", marginBottom: "16px" }} className="text-2xl sm:text-3xl lg:text-4xl">
            Trading Across Platforms Is{" "}<span style={{ color: "#EF4444" }}>Fragmented</span>
          </h2>
          <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "15px", maxWidth: "560px", marginBottom: "48px" }}>
            Every new platform you connect makes it harder to see the full picture. Your capital is spread, your risk is invisible, and your reactions are delayed.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {pains.map((pain, i) => (
            <FadeIn key={pain.title} delay={i * 0.1}>
              <OctaCard hover className="p-6 h-full">
                <div className="w-9 h-9 flex items-center justify-center mb-4" style={{ clipPath: CLIP_6, background: "rgba(239,68,68,0.06)" }}>
                  <pain.icon size={16} style={{ color: "#EF4444" }} strokeWidth={1.5} />
                </div>
                <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "14px", marginBottom: "8px" }}>{pain.title}</h3>
                <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#475569", fontSize: "13px", lineHeight: 1.6 }}>{pain.desc}</p>
              </OctaCard>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.4}>
          <p className="mt-12 text-center" style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "15px", fontStyle: "italic" }}>
            The more platforms you use, the harder it becomes to stay in control.
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

// ─── 4. SOLUTION ───────────────────────────────────────────────────
function SolutionSection() {
  const cards = [
    { icon: Layers, title: "Unified View", desc: "All platforms, all positions, all equity in one structured command screen. No more tab switching.", color: "#2563EB" },
    { icon: Zap, title: "Instant Control", desc: "Close, reduce, or monitor any position on any platform from one interface. Seconds, not minutes.", color: "#22C55E" },
    { icon: ShieldCheck, title: "Real-Time Risk", desc: "See your combined margin, exposure, and risk across every connected platform simultaneously.", color: "#38BDF8" },
  ];

  return (
    <section className="py-24 sm:py-32 relative" id="solution" data-testid="solution-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <FadeIn className="text-center mb-16">
          <SectionLabel text="The Solution" />
          <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", marginBottom: "12px" }} className="text-2xl sm:text-3xl lg:text-4xl">
            Meet <span style={{ color: "#2563EB" }}>ACE OS</span>
          </h2>
          <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "15px", maxWidth: "600px", margin: "0 auto" }}>
            A unified trading command system that sits above your existing platforms and gives you one structured view of your capital, positions, and actions.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map((card, i) => (
            <FadeIn key={card.title} delay={i * 0.12}>
              <OctaCard hover className="p-7 h-full">
                <div className="w-11 h-11 flex items-center justify-center mb-5" style={{ clipPath: CLIP_6, background: `${card.color}10` }}>
                  <card.icon size={20} style={{ color: card.color }} strokeWidth={1.5} />
                </div>
                <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "16px", marginBottom: "10px" }}>{card.title}</h3>
                <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "14px", lineHeight: 1.7 }}>{card.desc}</p>
              </OctaCard>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 5. PRODUCT PREVIEW ────────────────────────────────────────────
function ProductPreviewSection() {
  const positions = [
    { symbol: "BTCUSDT", side: "LONG", pnl: "+619.53", pct: "+18.43%", pos: true, entry: "$67,234", mark: "$68,473", lev: "10x" },
    { symbol: "ETHUSDT", side: "SHORT", pnl: "+301.68", pct: "+19.62%", pos: true, entry: "$3,845", mark: "$3,694", lev: "5x" },
    { symbol: "SOLUSDT", side: "LONG", pnl: "-555.00", pct: "-18.00%", pos: false, entry: "$185", mark: "$173", lev: "3x" },
  ];

  return (
    <section className="py-24 sm:py-32 relative" id="product-preview" data-testid="product-preview-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <FadeIn className="text-center mb-16">
          <SectionLabel text="Product Preview" />
          <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", marginBottom: "12px" }} className="text-2xl sm:text-3xl lg:text-4xl">
            Built Like a <span style={{ color: "#2563EB" }}>Command Center</span>
          </h2>
          <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "15px" }}>Not a dashboard. A precision instrument for multi-platform trading.</p>
        </FadeIn>

        <FadeIn delay={0.2}>
          <div className="relative" style={{ border: "1px solid rgba(255,255,255,0.05)", borderRadius: "4px", background: "rgba(11,13,16,0.7)", backdropFilter: "blur(12px)", overflow: "hidden" }}>
            {/* Glow */}
            <div style={{ position: "absolute", top: "-50%", left: "30%", width: "500px", height: "400px", background: "radial-gradient(circle, rgba(37,99,235,0.05), transparent 70%)", pointerEvents: "none" }} />

            <div className="p-6 sm:p-8">
              {/* Fake command wall header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", fontSize: "13px", color: "#E2E8F0", letterSpacing: "0.08em", textTransform: "uppercase" }}>COMMAND WALL</div>
                  <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", color: "#475569", letterSpacing: "0.08em", marginTop: "2px" }}>3 CONNECTED PLATFORMS</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
                    <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", color: "#22C55E", letterSpacing: "0.12em", fontWeight: "600" }}>SYSTEMS ONLINE</span>
                  </div>
                </div>
              </div>

              {/* Platform cards preview */}
              <LiveProductMockup />

              {/* Fake workspace preview - position cards */}
              <div className="mt-6 pt-6" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                <div className="flex items-center gap-3 mb-4">
                  <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", fontWeight: "600", letterSpacing: "0.16em", color: "#475569", textTransform: "uppercase" }}>BINANCE WORKSPACE · OPEN POSITIONS</span>
                  <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.04)" }} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {positions.map((pos, i) => (
                    <div key={i} style={{ clipPath: CLIP_8, background: "rgba(255,255,255,0.03)", padding: "1px" }}>
                      <div style={{ clipPath: CLIP_8, background: "linear-gradient(180deg, rgba(22,25,32,1), rgba(15,17,21,1))" }} className="p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "12px" }}>{pos.symbol}</span>
                          <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", fontWeight: "600", color: pos.side === "LONG" ? "#22C55E" : "#EF4444", letterSpacing: "0.08em" }}>{pos.side}</span>
                        </div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: "700", fontSize: "18px", color: pos.pos ? "#22C55E" : "#EF4444", fontVariantNumeric: "tabular-nums" }}>{pos.pnl}</div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: pos.pos ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)", fontVariantNumeric: "tabular-nums", marginTop: "2px" }}>{pos.pct}</div>
                        <div style={{ height: "1px", background: "rgba(255,255,255,0.04)", margin: "10px 0 8px" }} />
                        <div className="flex gap-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#475569", fontVariantNumeric: "tabular-nums" }}>
                          <span>E: {pos.entry}</span>
                          <span>M: {pos.mark}</span>
                          <span style={{ color: "#38BDF8" }}>{pos.lev}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─── 6. HOW IT WORKS ───────────────────────────────────────────────
function HowItWorksSection() {
  const steps = [
    { num: "01", icon: Plug, title: "Connect Your Platform", desc: "Securely link your exchange or broker using API credentials. Demo mode available for exploration.", color: "#2563EB" },
    { num: "02", icon: Layers, title: "See Everything in One View", desc: "Your positions, balances, and exposure appear inside one unified command screen across all connected platforms.", color: "#22C55E" },
    { num: "03", icon: Zap, title: "Take Action Instantly", desc: "Close, reduce, or monitor positions without switching between platforms. Full control from one interface.", color: "#38BDF8" },
  ];

  return (
    <section className="py-24 sm:py-32 relative" id="how-it-works" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <FadeIn className="text-center mb-16">
          <SectionLabel text="Getting Started" />
          <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0" }} className="text-2xl sm:text-3xl lg:text-4xl">
            Get Started in <span style={{ color: "#2563EB" }}>Minutes</span>
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-[60px] left-[16.6%] right-[16.6%] h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(37,99,235,0.2), rgba(37,99,235,0.2), transparent)" }} />

          {steps.map((step, i) => (
            <FadeIn key={step.num} delay={i * 0.15}>
              <div className="text-center">
                <div className="relative inline-block mb-6">
                  <OctaCard className="w-20 h-20 flex items-center justify-center">
                    <step.icon size={28} style={{ color: step.color }} strokeWidth={1.5} />
                  </OctaCard>
                  <div className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center" style={{ clipPath: CLIP_6, background: step.color }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", fontWeight: "700", color: "#fff" }}>{step.num}</span>
                  </div>
                </div>
                <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "15px", marginBottom: "8px" }}>{step.title}</h3>
                <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "13px", lineHeight: 1.6, maxWidth: "280px", margin: "0 auto" }}>{step.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 7. CAPABILITIES ───────────────────────────────────────────────
function CapabilitiesSection() {
  const caps = [
    { icon: Globe, title: "Multi-Platform Portfolio", desc: "See all your trading accounts in one view. Crypto, forex, equities.", color: "#2563EB" },
    { icon: Zap, title: "Position Control", desc: "Close, reduce, or manage any position on any platform from one interface.", color: "#22C55E" },
    { icon: ShieldCheck, title: "Risk Monitoring", desc: "Track margin usage, liquidation distances, and cross-platform exposure in real-time.", color: "#EF4444" },
    { icon: Radio, title: "Platform Health", desc: "Connection status, sync indicators, and latency monitoring for each venue.", color: "#38BDF8" },
    { icon: ScrollText, title: "Execution Logs", desc: "Full audit trail of every action taken across all platforms with timestamps.", color: "#F59E0B" },
    { icon: Cpu, title: "Connector Architecture", desc: "Binance, Zerodha, Alpaca, and MT5 live now. Upstox, Angel One, IB, and Tradier next.", color: "#A855F7" },
  ];

  return (
    <section className="py-24 sm:py-32 relative" id="capabilities" data-testid="capabilities-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <FadeIn className="mb-16">
          <SectionLabel text="Capabilities" />
          <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", marginBottom: "12px" }} className="text-2xl sm:text-3xl lg:text-4xl">
            One Interface. <span style={{ color: "#2563EB" }}>Multiple Layers</span> of Control.
          </h2>
          <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "15px", maxWidth: "480px" }}>
            Architected for crypto exchanges, forex brokers, and equity platforms. Binance, Zerodha, Alpaca, and MT5 are the first live connectors — more from day one.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {caps.map((cap, i) => (
            <FadeIn key={cap.title} delay={i * 0.08}>
              <OctaCard hover className="p-6 h-full">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center" style={{ clipPath: CLIP_6, background: `${cap.color}10` }}>
                    <cap.icon size={18} style={{ color: cap.color }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "14px", marginBottom: "6px" }}>{cap.title}</h3>
                    <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#475569", fontSize: "13px", lineHeight: 1.6 }}>{cap.desc}</p>
                  </div>
                </div>
              </OctaCard>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 8. TRUST ──────────────────────────────────────────────────────
function TrustSection() {
  const trust = [
    { icon: Lock, title: "Secure API Connection", desc: "Connect via read-only or trade-enabled API keys. Standard exchange permissions." },
    { icon: Shield, title: "No Custody", desc: "ACE OS never holds your funds. Your capital stays in your exchange accounts." },
    { icon: KeyRound, title: "No Withdrawal Access", desc: "API permissions never include withdrawal rights. Your assets are untouchable." },
    { icon: Server, title: "Encrypted Credentials", desc: "All API keys are encrypted with Fernet symmetric encryption at rest." },
    { icon: FileText, title: "Backend-Only Signing", desc: "Trade signing happens server-side only. Keys never touch the browser." },
    { icon: Terminal, title: "Full Activity Logs", desc: "Every action, every sync, every event is logged and auditable in real-time." },
  ];

  return (
    <section className="py-24 sm:py-32 relative" id="trust" data-testid="trust-section">
      {/* Subtle background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div style={{ position: "absolute", top: "20%", right: "10%", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(34,197,94,0.03), transparent 70%)", filter: "blur(80px)" }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
        <FadeIn className="text-center mb-16">
          <SectionLabel text="Security" />
          <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", marginBottom: "12px" }} className="text-2xl sm:text-3xl lg:text-4xl">
            Built for <span style={{ color: "#22C55E" }}>Serious Traders</span>
          </h2>
          <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "15px", maxWidth: "520px", margin: "0 auto" }}>
            Security is not a feature. It is the foundation. ACE OS is designed with institutional-grade security principles from the ground up.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {trust.map((t, i) => (
            <FadeIn key={t.title} delay={i * 0.08}>
              <OctaCard hover className="p-6 h-full">
                <div className="w-9 h-9 flex items-center justify-center mb-4" style={{ clipPath: CLIP_6, background: "rgba(34,197,94,0.06)" }}>
                  <t.icon size={16} className="text-[#22C55E]" strokeWidth={1.5} />
                </div>
                <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "14px", marginBottom: "6px" }}>{t.title}</h3>
                <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#475569", fontSize: "13px", lineHeight: 1.6 }}>{t.desc}</p>
              </OctaCard>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 9. FUTURE ─────────────────────────────────────────────────────
function FutureSection() {
  const layers = [
    { label: "PHASE 1 · NOW", title: "Unified Control Layer", items: ["Binance Futures", "Zerodha (Kite)", "Alpaca", "MetaTrader 5", "Demo Mode"], color: "#2563EB", active: true },
    { label: "PHASE 2 · NEXT", title: "Multi-Venue Expansion", items: ["Upstox", "Angel One", "Interactive Brokers", "Tradier", "WebSocket Streaming"], color: "#38BDF8", active: false },
    { label: "PHASE 3 · FUTURE", title: "Intelligence Stack", items: ["Smart Execution", "Portfolio Analytics", "Cross-Platform Alerts", "Risk Threshold Rules"], color: "#A855F7", active: false },
  ];

  return (
    <section className="py-24 sm:py-32 relative" id="roadmap" data-testid="roadmap-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <FadeIn className="mb-16">
          <SectionLabel text="Roadmap" />
          <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", marginBottom: "12px" }} className="text-2xl sm:text-3xl lg:text-4xl">
            The Foundation for a <span style={{ color: "#2563EB" }}>Larger Trading Stack</span>
          </h2>
          <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "15px", maxWidth: "520px" }}>
            ACE OS begins as a unified control layer and expands into a full trading operating stack.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {layers.map((layer, i) => (
            <FadeIn key={layer.label} delay={i * 0.15}>
              <div className="relative h-full">
                <OctaCard className={`p-6 h-full ${layer.active ? "" : ""}`}>
                  {layer.active && (
                    <div className="absolute top-3 right-3 flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
                      <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "7px", color: "#22C55E", letterSpacing: "0.12em", fontWeight: "600" }}>ACTIVE</span>
                    </div>
                  )}
                  <div className="mb-4">
                    <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", fontWeight: "600", letterSpacing: "0.16em", color: layer.color }}>{layer.label}</span>
                  </div>
                  <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "16px", marginBottom: "16px" }}>{layer.title}</h3>
                  <div className="space-y-3">
                    {layer.items.map(item => (
                      <div key={item} className="flex items-center gap-3">
                        <div className="w-1 h-1 rounded-full" style={{ background: layer.color }} />
                        <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "13px", color: layer.active ? "#94A3B8" : "#475569" }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </OctaCard>
                {/* Vertical connector line */}
                {layer.active && <div className="hidden md:block absolute -right-2.5 top-1/2 w-5 h-px" style={{ background: `linear-gradient(90deg, ${layer.color}40, transparent)` }} />}
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 10. FINAL CTA ─────────────────────────────────────────────────
function FinalCTASection() {
  return (
    <section className="py-24 sm:py-32 relative" data-testid="final-cta-section">
      {/* Background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div style={{ position: "absolute", top: "30%", left: "30%", width: "600px", height: "400px", background: "radial-gradient(circle, rgba(37,99,235,0.06), transparent 70%)", filter: "blur(100px)" }} />
      </div>

      <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center relative">
        <FadeIn>
          <div className="w-14 h-14 mx-auto mb-6 flex items-center justify-center bg-[#2563EB]" style={{ clipPath: CLIP_16 }}>
            <Zap size={24} className="text-white" strokeWidth={2} />
          </div>
          <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#fff", marginBottom: "16px" }} className="text-2xl sm:text-3xl lg:text-4xl">
            Take Back Control of Your<br /><span style={{ color: "#2563EB" }}>Trading Universe</span>
          </h2>
          <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "16px", lineHeight: 1.7, marginBottom: "32px", maxWidth: "480px", margin: "0 auto 32px" }}>
            Join the first users of ACE OS and experience a new way to monitor and control capital across platforms.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/signup" data-testid="final-cta-btn"
              className="flex items-center gap-2 transition-all duration-200 hover:brightness-110 hover:gap-3"
              style={{ clipPath: CLIP_8, background: "#2563EB", padding: "16px 36px", fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "13px", letterSpacing: "0.1em", color: "#fff" }}>
              GET EARLY ACCESS <ArrowRight size={15} />
            </Link>
            <Link to="/login" data-testid="final-demo-btn"
              className="flex items-center gap-2 transition-all duration-200 hover:text-[#E2E8F0]"
              style={{ clipPath: CLIP_8, border: "1px solid rgba(255,255,255,0.1)", padding: "16px 28px", fontFamily: "'Inter Tight', sans-serif", fontWeight: "500", fontSize: "13px", color: "#94A3B8" }}>
              REQUEST DEMO
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─── 11. FOOTER ────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="py-12 relative" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }} data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 flex items-center justify-center bg-[#2563EB]" style={{ clipPath: CLIP_6 }}>
              <Zap size={12} className="text-white" strokeWidth={2.5} />
            </div>
            <div>
              <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", fontSize: "12px", letterSpacing: "0.12em", color: "#fff" }}>ACE OS</span>
              <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#475569", marginLeft: "12px" }}>Unified trading command system</span>
            </div>
          </div>
          <div className="flex items-center gap-6">
            {["Product", "Security", "Contact", "Terms", "Privacy"].map(item => (
              <button key={item} className="transition-colors hover:text-[#94A3B8]" style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#475569" }}>
                {item}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-8 text-center">
          <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#334155" }}>
            2026 ACE OS. All rights reserved.
          </span>
          <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#334155", display: "block", marginTop: "4px" }}>
            Operated by <a href="https://www.gftworld.in" target="_blank" rel="noopener noreferrer" className="hover:text-[#94A3B8] transition-colors" style={{ color: "#475569", textDecoration: "underline", textUnderlineOffset: "2px" }}>www.gftworld.in</a>
          </span>
        </div>
      </div>
    </footer>
  );
}

// ─── LANDING PAGE ──────────────────────────────────────────────────
export default function Landing() {
  return (
    <div style={{ background: "#0B0D10", minHeight: "100vh", position: "relative" }} data-testid="landing-page">
      {/* Global background */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <div className="ace-bg-radial" style={{ position: "absolute", inset: 0 }} />
        <div className="ace-noise" />
      </div>

      <div className="relative" style={{ zIndex: 1 }}>
        <Header />
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <ProductPreviewSection />
        <HowItWorksSection />
        <CapabilitiesSection />
        <TrustSection />
        <FutureSection />
        <FinalCTASection />
        <Footer />
      </div>
    </div>
  );
}
